// src/pages/PrivacyPolicy/PrivacyPolicy.js

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

function AccordionItem({ item, isOpen, onToggle }) {
  return (
    <div
      className="
        overflow-hidden rounded-2xl
        border border-[rgb(var(--border))]
        bg-[rgb(var(--surface))]
        shadow-sm
      "
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="
          flex w-full items-start justify-between gap-4
          px-5 py-5 text-left
          transition-colors
          hover:bg-[rgb(var(--elevated))]
          focus:outline-none
          focus:ring-2
          focus:ring-inset
          focus:ring-[rgb(var(--brand)/0.45)]
        "
      >
        <div className="min-w-0">
          <div className="text-[15px] font-extrabold tracking-tight text-[rgb(var(--text-main))] sm:text-base">
            {item.q}
          </div>

          {item.short ? (
            <div className="mt-1.5 text-sm leading-6 text-[rgb(var(--text-muted))]">
              {item.short}
            </div>
          ) : null}
        </div>

        <span
          className="shrink-0 pt-0.5 text-xl font-semibold text-[rgb(var(--text-muted))]"
          aria-hidden="true"
        >
          {isOpen ? "−" : "+"}
        </span>
      </button>

      {isOpen ? (
        <div className="border-t border-[rgb(var(--border))] px-5 pb-5 pt-4">
          <div className="rounded-2xl bg-[rgb(var(--elevated))] px-4 py-4 sm:px-5">
            <div className="space-y-3 text-sm leading-7 text-[rgb(var(--text-main))]">
              {item.a.split("\n").map((line, i) => (
                <p key={i}>{line || "\u00A0"}</p>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function PrivacyPolicy() {
  const EFFECTIVE_DATE = "September 11, 2026";

  const items = useMemo(
    () => [
      {
        q: "1) About this policy",
        a: `This Privacy Policy explains how Veacon collects, uses, shares, retains, and protects information in connection with the Veacon mobile application, website, and related services (collectively, the “Service”).

Veacon helps people discover activities and events, make plans, connect with others, and create, host, join, or attend activities and events.

This Privacy Policy applies to information handled through the Service.`,
      },
      {
        q: "2) Information collected",
        a: `The information collected through Veacon depends on how the Service is used and which features are used.

Account and profile information may include:
• name
• username
• email address
• date of birth
• gender selection
• profile picture or avatar
• nickname
• bio
• account and profile settings

Information necessary to authenticate, verify, secure, and recover accounts may also be processed.

Use of Veacon's social features may involve information such as:
• friend relationships and friend requests
• invitations
• blocked users
• participation in plans, activities, or events
• social and discovery preferences

Creating, saving, joining, hosting, or interacting with activities, plans, or events may also involve associated information, including descriptions, times, locations, participation, invitations, logistics, and related content.`,
      },
      {
        q: "3) How information is used",
        a: `Information may be used to:
• create, maintain, authenticate, and secure accounts
• provide profiles and social features
• support friendships, invitations, activities, plans, hosting, and events
• provide discovery and recommendations
• provide nearby and location-based features
• remember preferences and settings
• send account, security, and service-related communications
• provide in-app or push notifications
• prevent spam, fraud, abuse, and unauthorized access
• review or investigate safety reports and suspicious behavior
• enforce the Veacon Terms of Service and applicable policies
• troubleshoot, maintain, secure, and improve Veacon
• comply with applicable legal obligations
• establish, exercise, or defend legal claims

Information may also be used for another purpose disclosed when the information is collected or with consent where consent is required.`,
      },
      {
        q: "4) Location and place information",
        a: `Some Veacon features use location or place information.

Depending on use of the Service, this may include:
• location information provided by the user
• places or addresses searched for or selected
• locations associated with activities, plans, or events
• saved places
• custom names assigned to saved places
• geographic coordinates where applicable
• location-related settings and preferences

Location information may be used to support maps, nearby discovery, events, plans, activities, saved places, and other location-based features.

Access to device location is subject to permissions granted through the device.

Device location permissions can be managed through device settings. Certain location-related preferences may also be available through Veacon.

Saved places and other location-related content may have visibility controls depending on the applicable feature.`,
      },
      {
        q: "5) Social features and visibility",
        a: `Veacon includes social features that involve sharing certain information with other users.

Depending on the feature and applicable visibility settings, other users may be able to see information such as:
• username
• profile picture
• profile information chosen for display
• activities, plans, or events created through Veacon
• invitations or participation information
• content chosen for sharing
• location information attached to shared content

Visibility varies by feature. Some content may be private, limited to friends or other permitted users, or designated as public.

The intended audience and information associated with content should be considered before that content is shared or made public.`,
      },
      {
        q: "6) Public profiles and public content",
        a: `Veacon provides limited public-facing features.

Certain basic profile information, such as a username and profile picture, may be publicly visible.

Events associated with an account may also be publicly accessible when those events are designated as public.

Information designated as private or restricted to a particular audience is not intended to become public solely because a Veacon account exists.

Content intentionally made public may be viewed by people who are not signed into Veacon and may be shared outside the Service.`,
      },
      {
        q: "7) Notifications and communications",
        a: `Veacon may provide in-app notifications and, where enabled, push notifications.

Notifications may relate to:
• friend requests and social interactions
• invitations
• hosted activities
• plans
• upcoming events
• event or plan updates and cancellations
• account or system information
• other service-related activity

Where push notifications require device permission, that permission can be managed through device settings.

An email address associated with a Veacon account may also be used for account-related communications, including:
• email verification
• password recovery
• security notices
• important service communications

Delivery of every notification or communication cannot be guaranteed.`,
      },
      {
        q: "8) Blocking, reporting, and safety",
        a: `Veacon provides tools intended to help users manage interactions with others.

Users may be able to block other users and report safety concerns, suspicious behavior, abuse, or potential violations of the Veacon Terms of Service or applicable policies.

Information processed in connection with a report may include:
• accounts involved
• reason for the report
• details submitted with the report
• status of the report
• information created or identified during review or moderation

This information may be used to review or investigate concerns, identify patterns of suspicious or abusive behavior, enforce applicable terms and policies, prevent abuse, protect users, and protect the Service.

Multiple, repeated, or related reports may result in additional review or investigation where potentially suspicious, abusive, unsafe, or prohibited behavior is identified.

Certain safety, moderation, or abuse-related information may be retained where reasonably necessary for safety, security, enforcement, dispute resolution, fraud prevention, or legal purposes.`,
      },
      {
        q: "9) Third-party events and services",
        a: `Veacon may display events and related information originating from third-party organizers, venues, event platforms, public sources, feeds, websites, or other providers.

This information may include:
• event names and descriptions
• dates and times
• venue and location information
• organizer information
• images
• pricing or ticket information
• registration links
• other event details

Some event listings or portions of listings may contain, incorporate, organize, reproduce, summarize, or otherwise present information originating from third-party sources.

Veacon may also use third-party service providers to support operation of the Service, including providers supporting:
• cloud and server infrastructure
• database services
• email delivery
• mapping and location features
• push notifications
• security and technical operations

Such providers may process information as necessary to provide their services in connection with Veacon and subject to applicable legal and contractual requirements.

Independent third-party websites and services have their own privacy practices. Information provided directly to an independent third party is subject to that third party's policies and practices.`,
      },
      {
        q: "10) Sharing of information",
        a: `Veacon does not sell or rent personal information.

Information may be shared or made available:
• to other users or the public when required by a feature or based on applicable visibility choices
• to service providers that support operation of Veacon
• when a user directs or authorizes information to be shared
• when reasonably necessary for safety or security
• when required by applicable law or legal process
• to investigate fraud, abuse, suspicious behavior, or violations of applicable terms or policies
• to protect the rights, property, safety, or security of Veacon, the Service, users, or others

If the operation or assets associated with Veacon become involved in a financing, acquisition, reorganization, sale, transfer, or similar transaction, information may be transferred as part of that transaction, subject to applicable law.`,
      },
      {
        q: "11) Data retention",
        a: `Information is retained for as long as reasonably necessary to provide the Service and fulfill the purposes described in this Privacy Policy.

Different categories of information may be retained for different periods depending on the purpose for which the information is needed.

Information may be retained as reasonably necessary to:
• maintain an account
• provide requested or used features
• maintain safety and security
• prevent abuse or fraud
• review or investigate safety reports
• resolve disputes
• enforce applicable agreements and policies
• comply with legal obligations

When information is no longer reasonably necessary for an applicable purpose, it may be deleted, anonymized, or otherwise disposed of in accordance with applicable law and operational requirements.`,
      },
      {
        q: "12) Account deletion",
        a: `A Veacon account may be permanently deleted through the account deletion feature provided in the app.

Identity or account verification may be required before deletion is completed in order to protect against unauthorized deletion requests.

When an account is deleted, the account and information associated with the existing Veacon presence are deleted or disassociated as applicable, including relevant profile, social, participation, activity, plan, event, notification, and account information.

Limited information may be retained where reasonably necessary for:
• safety and abuse prevention
• security
• fraud prevention
• legal compliance
• dispute resolution
• enforcement of the Veacon Terms of Service or applicable policies

Certain safety or abuse-related records, for example, may be retained where reasonably necessary to protect users or the Service.

Information originating independently from third parties, such as third-party event listings, does not necessarily depend on a Veacon account and may remain available independently of account deletion.`,
      },
      {
        q: "13) Choices and controls",
        a: `Depending on the applicable feature, Veacon provides controls that may allow users to:
• update profile information
• manage certain visibility settings
• manage discovery preferences
• control certain location-related preferences
• manage saved places
• manage notification permissions
• block or unblock users
• report users or content
• manage social relationships
• remove certain content
• permanently delete an account

Devices may provide additional controls for permissions such as location and notifications.`,
      },
      {
        q: "14) Security",
        a: `Reasonable administrative, technical, and organizational safeguards are used to protect information against unauthorized access, loss, misuse, alteration, or disclosure.

No method of transmission, storage, or information security is completely secure, and absolute security cannot be guaranteed.

Users are responsible for protecting their login credentials. Suspected unauthorized access or account compromise should be reported to support@veacon.app, and appropriate steps should be taken to secure the account.`,
      },
      {
        q: "15) Age requirements",
        a: `Veacon is intended for users who are at least 16 years old.

A person under 16 may not create or maintain a Veacon account.

If Veacon becomes aware that an account belongs to a person under 16, the account and associated information may be suspended, restricted, or deleted as appropriate.

Concerns regarding an account believed to belong to a person under 16 may be sent to support@veacon.app.`,
      },
      {
        q: "16) Privacy rights",
        a: `Depending on applicable law and where a user resides, certain rights may apply to personal information.

These rights may include the ability to request:
• access to certain personal information
• correction of inaccurate information
• deletion of certain information
• information about how personal information is processed

Requests may be subject to identity verification, legal exceptions, or other applicable requirements.

Privacy-related requests may be submitted to support@veacon.app.`,
      },
      {
        q: "17) Changes to this Privacy Policy",
        a: `This Privacy Policy may be updated from time to time to reflect changes to Veacon or applicable legal, regulatory, operational, safety, or security requirements.

When this Privacy Policy is updated, the effective date or “Last updated” date will be revised.

If a change is material, notice of the updated Privacy Policy will be made available on the applicable Veacon domain and through the Veacon platform where appropriate or required by applicable law.

Consent may be requested where required by applicable law.`,
      },
      {
        q: "18) Privacy support and requests",
        a: `Questions, concerns, privacy requests, or other communications relating to this Privacy Policy or Veacon's privacy practices may be sent to:

support@veacon.app

Veacon
veacon.app`,
      },
    ],
    []
  );

  const [openIndex, setOpenIndex] = useState(0);

  return (
  <div className="min-h-screen bg-[rgb(var(--bg-app))] text-[rgb(var(--text-main))]">
    <div
      className="h-1.5 w-full"
      style={{
        background:
          "linear-gradient(90deg, rgb(var(--brand)), rgb(var(--cool)), rgb(var(--event)))",
      }}
    />

    <main className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6 sm:py-14">
      <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-[rgb(var(--text-main))] sm:text-4xl">
            Privacy Policy
          </h1>

          <p className="mt-2 max-w-2xl text-sm leading-6 text-[rgb(var(--text-muted))] sm:text-base">
            This policy explains how Veacon collects, uses, shares, retains,
            and protects information.
          </p>

          <p className="mt-1 text-xs font-medium text-[rgb(var(--text-muted))] sm:text-sm">
            Effective date: {EFFECTIVE_DATE}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link
            to="/terms"
            className="
              rounded-xl
              border border-[rgb(var(--border))]
              bg-[rgb(var(--surface))]
              px-4 py-2.5
              text-sm font-bold
              text-[rgb(var(--text-main))]
              shadow-sm
              transition-colors
              hover:bg-[rgb(var(--elevated))]
            "
          >
            Terms
          </Link>

          <Link
            to="/"
            className="
              rounded-xl
              border border-[rgb(var(--border))]
              bg-[rgb(var(--surface))]
              px-4 py-2.5
              text-sm font-bold
              text-[rgb(var(--text-main))]
              shadow-sm
              transition-colors
              hover:bg-[rgb(var(--elevated))]
            "
          >
            Home
          </Link>
        </div>
      </div>

      <section
        className="
          rounded-3xl
          border border-[rgb(var(--border))]
          bg-[rgb(var(--surface))]
          p-3
          shadow-sm
          sm:p-5
        "
      >
        <div className="grid gap-3">
          {items.map((item, idx) => (
            <AccordionItem
              key={item.q}
              item={item}
              isOpen={openIndex === idx}
              onToggle={() =>
                setOpenIndex((prev) => (prev === idx ? -1 : idx))
              }
            />
          ))}
        </div>
      </section>

      <p className="mt-6 text-center text-xs leading-6 text-[rgb(var(--text-muted))] sm:text-sm">
        Questions about privacy?{" "}
        <a
          href="mailto:support@veacon.app"
          className="
            font-bold
            text-[rgb(var(--text-main))]
            underline
            decoration-[rgb(var(--border))]
            underline-offset-4
            transition-opacity
            hover:opacity-70
          "
        >
          support@veacon.app
        </a>
      </p>
    </main>
  </div>
);
}
